import { useEffect, useState } from 'react';
import { Link } from 'wouter';
import { works } from '../works';
import { NewsletterSignup } from './NewsletterSignup';

interface FooterProps {
  currentWorkSlug: string;
}

export function Footer({ currentWorkSlug }: FooterProps) {
  const [isWorksOpen, setIsWorksOpen] = useState(false);
  const [isProposalsOpen, setIsProposalsOpen] = useState(false);
  const isMenuOpen = isWorksOpen || isProposalsOpen;

  useEffect(() => {
    if (!isMenuOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsWorksOpen(false);
        setIsProposalsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  return (
    <>
      <footer
        className="fixed left-4 right-4 z-30 text-center text-base font-light text-black md:text-left md:text-xl"
        style={{ bottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}
      >
        <nav
          aria-label="Footer"
          className="mx-auto flex w-full max-w-[560px] items-center justify-center gap-2 md:mx-0 md:justify-start"
        >
          <button
            type="button"
            className="cursor-pointer border-0 bg-transparent p-0 text-left text-base font-light text-black transition-opacity duration-200 ease-in-out hover:opacity-70 md:text-xl"
            onClick={() => setIsWorksOpen(true)}
          >
            Works
          </button>
          <span aria-hidden="true">|</span>
          <Link
            href="/about"
            className="transition-opacity duration-200 ease-in-out hover:opacity-70"
          >
            About
          </Link>
          <span aria-hidden="true">|</span>
          {!isMenuOpen && (
            <NewsletterSignup
              triggerClassName="cursor-pointer border-0 bg-transparent p-0 text-left text-base font-light text-black transition-opacity duration-200 ease-in-out hover:opacity-70 md:text-xl"
              triggerStyle={{}}
            />
          )}
          <span aria-hidden="true">|</span>
          <button
            type="button"
            className="cursor-pointer border-0 bg-transparent p-0 text-left text-base font-light text-black transition-opacity duration-200 ease-in-out hover:opacity-70 md:text-xl"
            onClick={() => setIsProposalsOpen(true)}
          >
            Proposals
          </button>
        </nav>
      </footer>

      {isWorksOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-white p-4">
          <button
            type="button"
            className="absolute inset-0 cursor-default border-0 bg-transparent p-0"
            aria-label="Close works menu"
            onClick={() => setIsWorksOpen(false)}
          />
          <div
            className="absolute left-4 right-4 z-20 text-left text-base font-light text-black md:text-xl"
            style={{ bottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}
          >
            <button
              type="button"
              className="inline-block w-full max-w-[560px] cursor-pointer border-0 bg-transparent p-0 text-left text-base font-light text-black transition-opacity duration-200 ease-in-out hover:opacity-70 md:text-xl"
              onClick={() => setIsWorksOpen(false)}
            >
              Close
            </button>
          </div>
          <nav
            className="relative z-10 flex flex-col items-center gap-2 text-center text-base font-light text-black md:text-xl"
            aria-label="Works"
          >
            {works.map((work) => {
              const isCurrent = work.slug === currentWorkSlug;

              return (
                <Link
                  key={work.slug}
                  href={`/works/${work.slug}`}
                  className={`transition-opacity duration-200 ease-in-out hover:opacity-70 ${
                    isCurrent ? 'opacity-50' : ''
                  }`}
                  aria-current={isCurrent ? 'page' : undefined}
                  onClick={(event) => {
                    if (isCurrent) {
                      event.preventDefault();
                    }

                    setIsWorksOpen(false);
                  }}
                >
                  {work.title}
                </Link>
              );
            })}
          </nav>
        </div>
      )}

      {isProposalsOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-white p-4">
          <button
            type="button"
            className="absolute inset-0 cursor-default border-0 bg-transparent p-0"
            aria-label="Close proposals menu"
            onClick={() => setIsProposalsOpen(false)}
          />
          <div
            className="absolute left-4 right-4 z-20 text-left text-base font-light text-black md:text-xl"
            style={{ bottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}
          >
            <button
              type="button"
              className="inline-block w-full max-w-[560px] cursor-pointer border-0 bg-transparent p-0 text-left text-base font-light text-black transition-opacity duration-200 ease-in-out hover:opacity-70 md:text-xl"
              onClick={() => setIsProposalsOpen(false)}
            >
              Close
            </button>
          </div>
          <nav
            className="relative z-10 flex flex-col items-center gap-2 text-center text-base font-light text-black md:text-xl"
            aria-label="Proposals"
          >
            <Link
              href="/proposals/lapl-creators-in-residence"
              className="transition-opacity duration-200 ease-in-out hover:opacity-70"
              onClick={() => setIsProposalsOpen(false)}
            >
              LAPL Creators in Residence
            </Link>
          </nav>
        </div>
      )}
    </>
  );
}
